import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { Toaster, toast } from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

import Pagination from "../components/Pagination";
import { PDFViewer } from "@react-pdf/renderer";
import CustomerDetailsPDF from "../components/CustomerDetailsPDF";
import {
  isErpApiSuccess,
  postUserLedger,
  getErpRequestErrorMessage,
} from "../erpClient";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  buildReferenceSelectOptions,
  customerSelectMenuProps,
  fetchCustomerUserSearch,
} from "../utils/customerUserSelectOptions";

function CustomerLedger() {
  const { buttonClass } = useButtonTableStyle();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [loadingCustomerSearch, setLoadingCustomerSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const customerSearchTimerRef = useRef(null);

  const scheduleCustomerSearch = (rawInput) => {
    clearTimeout(customerSearchTimerRef.current);
    setCustomerSearchInput(rawInput);
    if (!String(rawInput ?? "").trim()) {
      setCustomerOptions([]);
      setLoadingCustomerSearch(false);
      return;
    }
    setLoadingCustomerSearch(true);
    customerSearchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(rawInput);
        setCustomerOptions(buildReferenceSelectOptions(rows));
      } catch {
        setCustomerOptions([]);
        toast.error("Failed to fetch customers");
      } finally {
        setLoadingCustomerSearch(false);
      }
    }, 400);
  };

  useEffect(() => {
    return () => {
      clearTimeout(customerSearchTimerRef.current);
    };
  }, []);

  // Listen for API URL changes and reset customer search + ledger
  useEffect(() => {
    const handleApiUrlChange = () => {
      clearTimeout(customerSearchTimerRef.current);
      setSelectedCustomer(null);
      setCustomerOptions([]);
      setCustomerSearchInput("");
      setLoadingCustomerSearch(false);
      setLedgerRows([]);
      setPdfData(null);
      setShowPDFModal(false);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const handleSubmit = async (
    e,
    { page = currentPage, limit = itemsPerPage } = {},
  ) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);

    const apiPayload = {
      uid: selectedCustomer.value,
    };

    if (entireReport) {
      apiPayload.type = "entire";
      apiPayload.page = page;
      apiPayload.limit = limit;
    } else {
      if (fromDate) apiPayload.from_date = fromDate;
      if (toDate) apiPayload.to_date = toDate;
    }
    try {
      const payload = await postUserLedger(apiPayload);

      if (!payload || (!isErpApiSuccess(payload) && payload.message !== "radha")) {
        toast.error(
          payload?.message || "Failed to fetch ledger data. Please try again."
        );
        console.error("Unexpected ledger response:", payload);
        setLedgerRows([]);
        return;
      }

      // Check if entries array exists in response
      const entries = payload.data?.entries || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found for this Customer / date range.");
        setLedgerRows([]);
        setTotalCount(0);
        return;
      }

      // Sort entries by date to ensure proper order
      const sortedEntries = [...entries].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() === dateB.getTime()) {
          return 0;
        }
        return dateA - dateB;
      });

      // Calculate opening balance from entries before the selected date range
      let openingAmountBalance = 0;
      let transactionsInPeriod = sortedEntries;

      if (!entireReport && fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);

        // Separate entries into opening balance (before fromDate) and period transactions
        const openingEntries = [];
        const periodEntries = [];

        sortedEntries.forEach((entry) => {
          const entryDate = new Date(entry.date);
          entryDate.setHours(0, 0, 0, 0);

          if (entryDate < from) {
            openingEntries.push(entry);
          } else if (!toDate || entryDate <= new Date(toDate)) {
            periodEntries.push(entry);
          }
        });

        // Calculate opening balance from entries before the period
        openingEntries.forEach((entry) => {
          openingAmountBalance +=
            (parseFloat(entry.aavak_amt) || 0) -
            (parseFloat(entry.jaavak_amt) || 0);
        });

        transactionsInPeriod = periodEntries;
      } else {
        // For entire report, opening balance starts at 0
        openingAmountBalance = 0;
      }

      let amountBalance = openingAmountBalance;

      const ledgerData = transactionsInPeriod.map((entry) => {
        const amount_credit = parseFloat(entry.aavak_amt) || 0;
        const amount_debit = parseFloat(entry.jaavak_amt) || 0;
        amountBalance += amount_credit - amount_debit;

        return {
          date: entry.date || "-",
          credit: amount_credit ? amount_credit.toFixed(2) : "",
          debit: amount_debit ? amount_debit.toFixed(2) : "",
          balance: amountBalance ? amountBalance.toFixed(2) : "0.00",
          remarks: entry.remarks || "",
          type: entry.type || "",
        };
      });

      const finalLedger = [
        ...(openingAmountBalance !== 0
          ? [
            {
              date: "-",
              type: "Opening Balance",
              credit: "",
              debit: "",
              balance: openingAmountBalance.toFixed(2),
              remarks: "",
              isOpening: true,
            },
          ]
          : []),
        ...ledgerData,
      ];
      setLedgerRows(finalLedger);
      setPdfData({
        ledgerData: finalLedger,
        customerInfo: {
          name: selectedCustomer?.label || "",
          mobile: selectedCustomer?.mobile || "",
          city: selectedCustomer?.city || "",
          address: selectedCustomer?.address || "",
        },
        fromDate,
        toDate,
        entireReport,
      });

      if (entireReport) {
        const paginationMeta = parseApiPagination(payload, finalLedger.length);
        setCurrentPage(paginationMeta.page);
        setTotalCount(paginationMeta.total);
        setHasMorePages(paginationMeta.hasMore);
        if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
      } else {
        setCurrentPage(1);
        setTotalCount(finalLedger.length);
        setHasMorePages(false);
      }

      toast.success("Ledger generated successfully!");
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while fetching the ledger data."
        )
      );
      console.error("Error fetching customer ledger:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const displayedRows = entireReport
    ? ledgerRows
    : ledgerRows.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      );

  const handlePrint = () => {
    if (!pdfData || !ledgerRows.length) {
      toast.error("Please generate the ledger first.");
      return;
    }
    setShowPDFModal(true);
  };

  const pdfViewer = useMemo(() => {
    if (!pdfData) return null;
    return (
      <PDFViewer width="100%" height="100%">
        <CustomerDetailsPDF
          ledgerData={pdfData.ledgerData}
          customerInfo={pdfData.customerInfo}
          fromDate={pdfData.fromDate}
          toDate={pdfData.toDate}
          entireReport={pdfData.entireReport}
        />
      </PDFViewer>
    );
  }, [pdfData]);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Customer Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* Customer Dropdown */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">Select Customer</label>
                  <Select
                    options={customerOptions}
                    value={selectedCustomer}
                    inputValue={customerSearchInput}
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        scheduleCustomerSearch(inputValue);
                      }
                      if (action === "menu-close" || action === "set-value") {
                        clearTimeout(customerSearchTimerRef.current);
                        setCustomerSearchInput("");
                        setCustomerOptions([]);
                        setLoadingCustomerSearch(false);
                      }
                    }}
                    onChange={(option) => {
                      setSelectedCustomer(option);
                      setCustomerSearchInput("");
                      setCustomerOptions([]);
                      setLoadingCustomerSearch(false);
                    }}
                    noOptionsMessage={() =>
                      loadingCustomerSearch
                        ? "Searching..."
                        : customerSearchInput.trim()
                          ? "No customers found"
                          : "Type name or mobile to search"
                    }
                    placeholder="Search and select Customer..."
                    isClearable
                    isSearchable
                    isLoading={loadingCustomerSearch}
                    filterOption={() => true}
                    {...customerSelectMenuProps}
                  />
                </div>
                {/* From Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={!selectedCustomer || loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {ledgerRows.length > 0 && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Ledger -{" "}
                    {selectedCustomer?.label ? selectedCustomer.label : ""}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
                <div className="table-responsive mb-4 border rounded-1">
                  <table className="table text-nowrap mb-0 align-middle">
                    <thead className="text-dark fs-4">
                      <tr>
                        <th className="text-nowrap">Date</th>
                        <th className="text-nowrap">Type</th>
                        <th className="text-end text-nowrap text-success">
                          Credit
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Debit
                        </th>
                        <th className="text-end text-nowrap">Balance</th>
                        <th className="text-nowrap">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedRows.map((row, idx) => {
                        const cellStyle = row.isOpening
                          ? { backgroundColor: "#E3F2FD", fontWeight: 500 }
                          : undefined;
                        return (
                          <tr key={idx}>
                            <td style={cellStyle}>
                              {row.isOpening ? "-" : row.date}
                            </td>
                            <td style={cellStyle}>{row.type || "-"}</td>
                            <td
                              className={`text-end ${row.credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.credit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.balance || "-"}
                            </td>
                            <td style={cellStyle}>{row.remarks || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  totalItems={entireReport ? totalCount : ledgerRows.length}
                  hasMore={entireReport ? hasMorePages : false}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={(page) => {
                    if (entireReport) {
                      handleSubmit(undefined, { page, limit: itemsPerPage });
                    } else {
                      setCurrentPage(page);
                    }
                  }}
                  onItemsPerPageChange={(limit) => {
                    if (entireReport) {
                      handleSubmit(undefined, { page: 1, limit });
                    } else {
                      setItemsPerPage(limit);
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Customer Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {showPDFModal && pdfData && pdfViewer}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CustomerLedger;
